import React, { useRef, useState } from 'react';
import Swal from 'sweetalert2';
import ApiService from '../../../services/ApiService';

const RoomAssignmentModal = ({
    show,
    onClose,
    onSuccess,
    onValidationError,
    onLoadingChange,
    roomNumber,
    confirmationNumber,
    pmsPkId,
    externalId
}) => {
    const [loading, setLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState(null);
    const [responseType, setResponseType] = useState(null); // 'success' or 'error'
    const finishingSuccessRef = useRef(false);

    const finishSuccess = async (apiMessage) => {
        finishingSuccessRef.current = true;
        setLoading(false);
        if (onLoadingChange) onLoadingChange(false);

        const title = apiMessage || `Room ${roomNumber} assigned successfully`;
        const text = 'Please allow up to one minute for room assignment to appear.';

        if (onSuccess) onSuccess();

        await Swal.fire({
            position: 'center',
            icon: 'success',
            title,
            text,
            showConfirmButton: false,
            timer: 2500,
            didOpen: () => {
                const container = document.querySelector('.swal2-container');
                if (container) {
                    container.style.zIndex = '10000';
                }
            }
        });
    };

    const handleConfirm = async () => {
        if (!pmsPkId || !externalId) {
            setResponseMessage('Missing required parameters');
            setResponseType('error');
            return;
        }

        setLoading(true);
        if (onLoadingChange) onLoadingChange(true);
        try {
            const response = await ApiService.put(
                '/assignroomnumber',
                {
                    pms_pk_id: pmsPkId,
                    external_id: externalId
                },
                {
                    timeout: 60000
                }
            );

            const responseData = response?.data || response;
            const responseCode = responseData?.code;
            const apiMessage = responseData?.message;

            if (responseCode === 'SUCCESS') {
                await finishSuccess(apiMessage);
                return;
            }
            if (responseCode === 'VALIDATION_ERROR') {
                const errorMessage = apiMessage || 'Room not available to assign';
                setResponseMessage(errorMessage);
                setResponseType('error');
                if (onValidationError) {
                    onValidationError(errorMessage);
                }
            } else {
                const errorMessage = apiMessage || 'Failed to assign room';
                setResponseMessage(errorMessage);
                setResponseType('error');
            }
        } catch (error) {
            console.error('Error assigning room:', error);

            if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                if (error?.response?.data) {
                    if (error.response.data.code === 'SUCCESS') {
                        await finishSuccess(error.response.data.message);
                        return;
                    }
                    if (error.response.data.code === 'VALIDATION_ERROR') {
                        const errorMessage = error.response.data.message || 'Room not available to assign';
                        setResponseMessage(errorMessage);
                        setResponseType('error');
                        if (onValidationError) {
                            onValidationError(errorMessage);
                        }
                        return;
                    }
                    const errorMessage = error.response.data.message || 'Failed to assign room';
                    setResponseMessage(errorMessage);
                    setResponseType('error');
                    return;
                }
                setResponseMessage(
                    'Request timed out. Please check if the room was assigned and try again if needed.'
                );
                setResponseType('error');
                return;
            }

            const errorResponseData = error?.response?.data || error?.response;
            const errorCode = errorResponseData?.code;
            const errorMessage = errorResponseData?.message;

            if (errorCode === 'SUCCESS') {
                await finishSuccess(errorMessage);
                return;
            }
            if (errorCode === 'VALIDATION_ERROR') {
                const validationErrorMessage = errorMessage || 'Room not available to assign';
                setResponseMessage(validationErrorMessage);
                setResponseType('error');
                if (onValidationError) {
                    onValidationError(validationErrorMessage);
                }
            } else if (errorResponseData) {
                const apiErrorMessage = errorMessage || 'Failed to assign room';
                setResponseMessage(apiErrorMessage);
                setResponseType('error');
            } else {
                const networkErrorMessage = error?.message || 'Failed to assign room. Please try again.';
                setResponseMessage(networkErrorMessage);
                setResponseType('error');
            }
        } finally {
            if (!finishingSuccessRef.current) {
                setLoading(false);
                if (onLoadingChange) onLoadingChange(false);
            }
            finishingSuccessRef.current = false;
        }
    };

    const handleClose = () => {
        setResponseMessage(null);
        setResponseType(null);
        onClose();
    };

    if (!show) return null;

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="roomAssignmentModalLabel"
            aria-hidden="true"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 2060,
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                padding: '0.75rem'
            }}
        >
            <div
                className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down"
                role="document"
                style={{ maxWidth: 560, width: 'calc(100% - 0.5rem)' }}
            >
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title" id="roomAssignmentModalLabel">
                            Assign Room {roomNumber}
                        </h5>
                        <button
                            type="button"
                            className="btn-close"
                            aria-label="Close"
                            onClick={handleClose}
                            disabled={loading}
                        ></button>
                    </div>
                    <div className="modal-body">
                        {responseMessage && responseType === 'error' ? (
                            <div className="alert alert-danger mb-0" style={{ fontSize: '1.1rem', fontWeight: 500 }}>
                                {responseMessage}
                            </div>
                        ) : (
                            <p
                                className="mb-0"
                                style={{
                                    wordBreak: 'break-word',
                                    fontSize: '1.1rem',
                                    fontWeight: 500,
                                    color: '#212529',
                                    lineHeight: 1.5
                                }}
                            >
                                Are you sure you want to assign <strong>Room {roomNumber}</strong> to confirmation{' '}
                                <strong>{confirmationNumber}</strong>?
                            </p>
                        )}
                    </div>
                    <div className="modal-footer justify-content-center border-0 gap-3 pt-0">
                        {responseMessage && responseType === 'error' ? (
                            <button type="button" className="btn btn-primary" onClick={handleClose}>
                                OK
                            </button>
                        ) : (
                            <>
                                <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={loading}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={loading}>
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Assigning...
                                        </>
                                    ) : (
                                        'Assign'
                                    )}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoomAssignmentModal;
